import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StickyNote, Trash2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

export const AdminNotes = ({ targetType, targetId, onChanged, testIdPrefix = "admin-notes" }) => {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/admin/notes`, { params: { target_type: targetType, target_id: targetId } })
      .then((r) => { if (active) setNotes(r.data.notes); })
      .catch(() => { if (active) setNotes([]); });
    return () => { active = false; };
  }, [targetType, targetId]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/notes`, { target_type: targetType, target_id: targetId, text: t });
      setNotes((n) => [r.data, ...(n || [])]);
      setText("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the note.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
      onChanged?.();
    } catch {
      toast.error("Couldn't delete the note.");
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-panel`}>
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] flex items-center gap-2">
        <StickyNote size={12} className="text-[#B45309]" /> Private notes · admins only
      </div>
      <div className="flex gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }}
          rows={2}
          maxLength={1000}
          placeholder="Jot context for the next follow-up…"
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 bg-[#FAF7F2] border border-[#E7DFD5] px-3 py-2 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B]/60 resize-none"
        />
        <button onClick={add} disabled={busy || !text.trim()} className="btn-ember text-xs self-end disabled:opacity-50 !px-3 !py-2" data-testid={`${testIdPrefix}-add`}>
          <Plus size={12} /> Add
        </button>
      </div>
      <ul className="space-y-2 max-h-56 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
        {notes && notes.length === 0 && <li className="text-xs text-[#555]" data-testid={`${testIdPrefix}-empty`}>No notes yet.</li>}
        {(notes || []).map((n) => (
          <li key={n.id} className="group flex items-start gap-2 border-l-2 border-[#F59E0B]/50 pl-3" data-testid={`${testIdPrefix}-item-${n.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#44403C] leading-relaxed whitespace-pre-wrap">{n.text}</p>
              <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#A8A29E] mt-0.5">{when(n.at)} · {n.by}</div>
            </div>
            <button onClick={() => remove(n.id)} aria-label="Delete note" className="p-1 text-[#555] hover:text-red-400 transition-colors" data-testid={`${testIdPrefix}-delete-${n.id}`}>
              <Trash2 size={13} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
